import { Link } from 'react-router-dom';
import JwalekLogo from '@/components/JwalekLogo';
import { Instagram, Twitter, Mail, MapPin, Sparkles } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const Footer = () => {
  const email = 'hello@jwalek.com';
  const ig = 'https://instagram.com/jwalek';
  const tw = 'https://twitter.com/jwalek';
  const { t } = useLanguage();
  const addr = t('footer.address', 'Riyadh, Kingdom of Saudi Arabia');
  const tagline = t('footer.tagline', 'Design it your way, we print it perfectly. Custom-printed phone cases, stickers and labels shipped fast.');

  return (
    <footer
      // pb-24 md:pb-0 leaves room for the mobile bottom nav so links aren't hidden.
      className="mt-20 pb-24 md:pb-0 border-t relative overflow-hidden"
      style={{ borderColor: 'var(--jw-border)' }}
    >
      <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-15 blur-3xl jw-gradient-bg" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 relative grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2 md:col-span-2">
          <JwalekLogo size={30} />
          <p className="text-sm mt-3 max-w-sm leading-relaxed" style={{ color: 'var(--jw-text-muted)' }}>{tagline}</p>
          <div className="flex items-center gap-2 text-xs mt-4" style={{ color: 'var(--jw-text-subtle)' }}>
            <MapPin className="w-3.5 h-3.5" /> {addr}
          </div>
          <a href={`mailto:${email}`} className="flex items-center gap-2 text-xs mt-1 hover:opacity-80" style={{ color: 'var(--jw-text-subtle)' }}>
            <Mail className="w-3.5 h-3.5" /> {email}
          </a>
          <div className="flex gap-2 mt-4">
            <a href={ig} target="_blank" rel="noreferrer noopener" className="p-2 rounded-lg jw-panel hover:opacity-80" data-testid="footer-instagram-link" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
            <a href={tw} target="_blank" rel="noreferrer noopener" className="p-2 rounded-lg jw-panel hover:opacity-80" data-testid="footer-twitter-link" aria-label="X (Twitter)"><Twitter className="w-4 h-4" /></a>
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-3 text-sm jw-text-primary">{t('nav.shop', 'Shop')}</h4>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--jw-text-muted)' }}>
            <li><Link to="/shop/phone_case" className="hover:opacity-80" data-testid="footer-phone-cases-link">{t('shop.phoneCases', 'Phone Cases')}</Link></li>
            <li><Link to="/shop/sticker" className="hover:opacity-80" data-testid="footer-stickers-link">{t('shop.stickers', 'Stickers')}</Link></li>
            <li><Link to="/shop/label" className="hover:opacity-80" data-testid="footer-labels-link">{t('shop.labels', 'Labels')}</Link></li>
            <li><Link to="/shop" className="hover:opacity-80" data-testid="footer-shop-all-link">{t('shop.all', 'Shop all')}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-3 text-sm jw-text-primary">{t('footer.explore', 'Create')}</h4>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--jw-text-muted)' }}>
            <li><Link to="/designs" className="hover:opacity-80" data-testid="footer-library-link">{t('nav.designs', 'Design Library')}</Link></li>
            <li><Link to="/community" className="hover:opacity-80" data-testid="footer-community-link">{t('nav.community', 'Community')}</Link></li>
            <li><Link to="/top-rated" className="hover:opacity-80" data-testid="footer-top-rated-link">{t('nav.topRated', 'Top Rated')}</Link></li>
            <li><Link to="/ai-generator" className="hover:opacity-80 inline-flex items-center gap-1" data-testid="footer-ai-link">
              {t('nav.aiGenerator', 'AI Generator')} <Sparkles className="w-3 h-3 text-orange-400" />
            </Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-3 text-sm jw-text-primary">{t('footer.support', 'Support')}</h4>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--jw-text-muted)' }}>
            <li><Link to="/legal/terms" data-testid="footer-terms-link" className="hover:opacity-80">{t('nav.terms', 'Terms & Conditions')}</Link></li>
            <li><Link to="/legal/privacy" data-testid="footer-privacy-link" className="hover:opacity-80">{t('nav.privacy', 'Privacy Policy')}</Link></li>
            <li><Link to="/legal/dmca" data-testid="footer-dmca-link" className="hover:opacity-80">{t('footer.dmca', 'DMCA')}</Link></li>
            <li><Link to="/contact" data-testid="footer-contact-link" className="hover:opacity-80">{t('nav.contact', 'Contact us')}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t relative" style={{ borderColor: 'var(--jw-border)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between text-xs flex-wrap gap-2" style={{ color: 'var(--jw-text-subtle)' }}>
          <span>© {new Date().getFullYear()} Jwalek. {t('footer.rights', 'All rights reserved.')}</span>
          <span>{t('footer.madeWith', 'Made with ❤️ in Riyadh')}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
